/* FSC Training - the registry of workflow guides.
 *
 * The single list both index.js (one route per guide) and the FSC Training
 * index page read from. ADDING A GUIDE: write the page under operations/ or
 * trading/ and add one row here. Nothing else - the routes are public and
 * outside the sidebar app, reached from links in the Resource Library, the
 * B2B portal and FSC. A guide's path must never change once it is linked.
 *
 * Grouped by the record type chosen on the Create New Case screen, in the
 * order FSC lists the Categories. Paths are <department>/<category>, plus a
 * /<sub-category> segment where a Sub-Category is a different enough task to
 * need its own page.
 */

import RothConversions from './operations/RothConversions';
import NewBusiness from './operations/NewBusiness';
import Contributions from './operations/Contributions';
import Distributions from './operations/Distributions';
import AddUpdatePaperwork from './operations/AddUpdatePaperwork';
import StaffChanges from './operations/StaffChanges';
import ClientInfoAddressContact from './operations/ClientInfoAddressContact';
import ClientInfoInvestmentObjective from './operations/ClientInfoInvestmentObjective';
import ClientInfoBankingAch from './operations/ClientInfoBankingAch';
import AccountTransfers from './operations/AccountTransfers';
import CancelContributionsDistributions from './operations/CancelContributionsDistributions';
import CloseAccounts from './operations/CloseAccounts';
import DeceasedClient from './operations/DeceasedClient';
import FeesBilling from './operations/FeesBilling';
import SalesforceListViews from './operations/SalesforceListViews';
import SalesforceNewLicense from './operations/SalesforceNewLicense';
import SalesforceReportProblem from './operations/SalesforceReportProblem';
import PlaceTradeOrder from './trading/PlaceTradeOrder';
import AllocateAccount from './trading/AllocateAccount';
import RaiseCash from './trading/RaiseCash';
import InvestCash from './trading/InvestCash';
import DollarCostAverage from './trading/DollarCostAverage';
import RaiseFundsForBilling from './trading/RaiseFundsForBilling';
import ModelManagement from './trading/ModelManagement';
import LiquidateAccounts from './trading/LiquidateAccounts';
import TradeErrorReview from './trading/TradeErrorReview';
import TradeRestrictions from './trading/TradeRestrictions';

const BLURB_TAIL = 'What to gather first, every question in the form, and what happens after you submit.';

export const GUIDES = [
  {
    department: 'Operations',
    icon: 'gear-wide-connected',
    pages: [
      { label: 'New Business', icon: 'person-plus', path: '/fsc-training/operations/new-business', Component: NewBusiness,
        route: 'Operations → New Business → New Business (New Client) or New Account (Existing Client).' },
      { label: 'Roth Conversions', icon: 'arrow-left-right', path: '/fsc-training/operations/roth-conversions', Component: RothConversions,
        route: 'Operations → Roth Conversion(s) → Full or Partial.' },
      { label: 'Contributions / New Money', icon: 'box-arrow-in-down', path: '/fsc-training/operations/contributions', Component: Contributions,
        route: 'Operations → Contributions / New Money → One-Time or Recurring.' },
      { label: 'Distributions / Move Money', icon: 'box-arrow-up', path: '/fsc-training/operations/distributions', Component: Distributions,
        route: 'Operations → Distributions / Move Money → One-Time or Recurring.' },
      { label: 'Add / Update Paperwork', icon: 'file-earmark-text', path: '/fsc-training/operations/add-update-paperwork', Component: AddUpdatePaperwork,
        route: 'Operations → Add / Update Paperwork → Add / Update Paperwork or Missing Paperwork Follow-Up.' },
      { label: 'Staff Changes / Updates', icon: 'people', path: '/fsc-training/operations/staff-changes', Component: StaffChanges,
        route: 'Operations → Staff Changes / Updates → New or Terminated Advisor / CSA / Staff.' },
      { label: 'Address / Contact Update', icon: 'person-lines-fill', path: '/fsc-training/operations/client-information-change/address-contact', Component: ClientInfoAddressContact,
        route: 'Operations → Client Information Change → Address / Contact Update.' },
      { label: 'Investment Objective / Risk Update', icon: 'speedometer2', path: '/fsc-training/operations/client-information-change/investment-objective-risk', Component: ClientInfoInvestmentObjective,
        route: 'Operations → Client Information Change → Investment Objective / Risk Update.' },
      { label: 'Banking / ACH Instructions Update', icon: 'bank', path: '/fsc-training/operations/client-information-change/banking-ach', Component: ClientInfoBankingAch,
        route: 'Operations → Client Information Change → Banking / ACH Instructions Update.' },
      { label: 'Account Transfer of Assets', icon: 'box-arrow-in-right', path: '/fsc-training/operations/account-transfers', Component: AccountTransfers,
        route: 'Operations → Account Transfer of Assets → Transfer of Assets (TOA) or Account Transfer (ACAT).' },
      { label: 'Cancel Contributions / Distributions', icon: 'x-circle', path: '/fsc-training/operations/cancel-contributions-distributions', Component: CancelContributionsDistributions,
        route: 'Operations → Cancel Contributions / Distributions → One-Time or Recurring.' },
      { label: 'Close Account(s)', icon: 'door-closed', path: '/fsc-training/operations/close-accounts', Component: CloseAccounts,
        route: 'Operations → Close Account(s) → Close Accounts (ALL) or Close Accounts (Some).' },
      { label: 'Deceased Client', icon: 'person-x', path: '/fsc-training/operations/deceased-client', Component: DeceasedClient,
        route: 'Operations → Deceased Client → Deceased Client.' },
      { label: 'Fees / Billing', icon: 'receipt', path: '/fsc-training/operations/fees-billing', Component: FeesBilling,
        route: 'Operations → Fees / Billing → Fee Refund, Fee Schedule Change or Fee Adjustment (One-Time).' },
      { label: 'Salesforce List Views', icon: 'table', path: '/fsc-training/operations/salesforce/list-views', Component: SalesforceListViews,
        route: 'Operations → Salesforce → New List View or Modify ListView.' },
      { label: 'Salesforce License', icon: 'person-badge', path: '/fsc-training/operations/salesforce/new-license', Component: SalesforceNewLicense,
        route: 'Operations → Salesforce → New License.' },
      { label: 'Report a Salesforce Problem', icon: 'bug', path: '/fsc-training/operations/salesforce/report-problem', Component: SalesforceReportProblem,
        route: 'Operations → Salesforce → Report Problem.' },
    ],
  },
  {
    department: 'Trading',
    icon: 'graph-up',
    pages: [
      { label: 'Place Trade Order', icon: 'arrow-down-up', path: '/fsc-training/trading/place-trade-order', Component: PlaceTradeOrder,
        route: 'Trading → Place Trade Order → Buy Order, Sell Order or Buy / Sell Order.' },
      { label: 'Allocate Account', icon: 'pie-chart', path: '/fsc-training/trading/allocate-account', Component: AllocateAccount,
        route: 'Trading → Allocate Account → Allocate New Account / Sleeves or Re-Allocate / Modify Existing Accounts.' },
      { label: 'Raise Cash', icon: 'cash-coin', path: '/fsc-training/trading/raise-invest-cash/raise-cash', Component: RaiseCash,
        route: 'Trading → Raise / Invest Cash → Raise Cash.' },
      { label: 'Invest Cash', icon: 'graph-up-arrow', path: '/fsc-training/trading/raise-invest-cash/invest-cash', Component: InvestCash,
        route: 'Trading → Raise / Invest Cash → Invest Cash.' },
      { label: 'Dollar Cost Average', icon: 'calendar-range', path: '/fsc-training/trading/dollar-cost-average', Component: DollarCostAverage,
        route: 'Trading → Dollar Cost Average → Dollar Cost Average.' },
      { label: 'Raise Funds for Billing', icon: 'receipt-cutoff', path: '/fsc-training/trading/raise-funds-for-billing', Component: RaiseFundsForBilling,
        route: 'Trading → Raise Funds for Billing → Fee Raise (One-Time) or Fee Raise (Recurring).' },
      { label: 'Model Management', icon: 'diagram-3', path: '/fsc-training/trading/model-management', Component: ModelManagement,
        route: 'Trading → Model Management → Rebalance Model(s), Create New Model(s) or Edit Existing Model(s).' },
      { label: 'Liquidate Account(s)', icon: 'cash-stack', path: '/fsc-training/trading/liquidate-accounts', Component: LiquidateAccounts,
        route: 'Trading → Liquidate Account(s) → Liquidate Specific Account(s) or Liquidate All Client Account(s).' },
      { label: 'Trade Error / Trade Review', icon: 'exclamation-octagon', path: '/fsc-training/trading/trade-error-review', Component: TradeErrorReview,
        route: 'Trading → Trade Error / Trade Review → Trade Error or Trade Review.' },
      { label: 'Trade Restrictions / Do Not Trade', icon: 'slash-circle', path: '/fsc-training/trading/trade-restrictions', Component: TradeRestrictions,
        route: 'Trading → Trade Restrictions / Do Not Trade → Add Trade Restriction, Remove Trade Restriction or Do Not Trade.' },
    ],
  },
].map(group => ({
  ...group,
  pages: group.pages.map(page => ({ ...page, blurb: `${page.route} ${BLURB_TAIL}` })),
}));

// Flat list for the router.
export const GUIDE_ROUTES = GUIDES.flatMap(group => group.pages);
